import {
  Component,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import {BaseComponent} from '../../base.component';
import {SelectAdditivesComponent} from '../dialog/select-additives/select-additives.component';
import {isEqual} from 'lodash';
import {strings} from '../../../../value/strings';
import {ListResponse} from '../../../network/service/list.response';
import {SnackBarUtil} from '../../../util/snack.bar.util';
import {StringUtil} from '../../../util/string.util';
import {RequestType} from '../../../enums/request.type';
import {SessionStorageUtil} from '../../../util/session.storage.util';
import {DataResponse} from '../../../network/service/data.response';
import {Config} from '../../../common/config';
import {DeviceService} from '../../../util/service/device.service';
import {RouteUtil} from '../../../util/route.util';

@Component({
  selector: 'app-basket',
  standalone: false,
  templateUrl: './basket.component.html',
  styleUrl: './basket.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class BasketComponent extends BaseComponent implements OnInit, OnChanges {
  @Input()
  orders?: any = [];
  @Input()
  isLoading: boolean = true;
  @Output()
  ordersChange: EventEmitter<any> = new EventEmitter();
  isBasketFinalStep: boolean = false;
  isMobile?: boolean;
  paymentMethodeId?: any;
  selectedUserAddress?: any;
  deliveryMethodeId?: any;
  basketSkeletonPlaceholders: number[] = Array.from({length: 2}, (_, index) => index);
  checkoutSkeletonPlaceholders: number[] = Array.from({length: 3}, (_, index) => index);
  protected readonly StringUtil = StringUtil;
  protected readonly RouteUtil = RouteUtil;
  protected readonly Config = Config;

  constructor(injector: Injector, deviceService: DeviceService) {
    super(injector);
    this.isMobile = deviceService.isMobile();
  }

  override ngOnInit() {
    super.ngOnInit();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['orders'] && changes['orders'].currentValue) {
      this.normalizeOrders();
      this.isLoading = false;
    }
  }

  normalizeOrders() {
    if (this.orders && Array.isArray(this.orders)) {
      this.orders = this.orders.map((order: any) => {
        if (!order.ServicePriceAdditiveIds && order.OrderServicePriceAdditives?.length > 0) {
          order.ServicePriceAdditiveIds = order.OrderServicePriceAdditives.map((osa: any) =>
            osa.ServicePriceAdditive?.ServicePriceAdditiveId
          ).filter((id: any) => id != null);
        }
        return order;
      });
    }
  }

  override ngAfterViewInit() {
    super.ngAfterViewInit();
  }

  onShowPaymentClicked() {
    if (this.isMobile) {
      if (!this.isBasketFinalStep) {
        this.isBasketFinalStep = !this.isBasketFinalStep;
      } else {
        this.serviceApi.createFactor({
          DeliveryMethodId: this.deliveryMethodeId,
          UserAddressId: this.selectedUserAddress?.UserAddressId,
          PaymentMethodId: this.paymentMethodeId
        }).subscribe((res: DataResponse<any>) => {
          if (res.success) {
            window.location.href = res.data;
          } else {
            SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!), res.success, res.data);
          }
        });
      }
    } else {
      this.router.navigate([RouteUtil.CLIENT_MODULE, RouteUtil.BASKET_COMPONENT, Config.branch.UniqueIdentifier]).then();
    }
  }

  override loadOnline() {
    super.loadOnline();
    this.isLoading = true;
    if (this.hasAccount()) {
      this.serviceApi.getMyOrders(undefined, undefined, 'ServicePrice,ServicePrice.Service,ServicePrice.ServicePriceAdditives,ServicePrice.ServicePriceAdditives.Additive,ServicePrice.ServicePriceAdditives.Attribute,OrderServicePriceAdditives,OrderServicePriceAdditives.ServicePriceAdditive,OrderServicePriceAdditives.ServicePriceAdditive.Additive,OrderServicePriceAdditives.ServicePriceAdditive.Attribute,OrderServicePriceAdditives.ServicePriceAdditive.Attribute.AttributeType', RequestType.ALL, Config.branch.BusinessId, {
        Filter: {
          Values: [{
            Field: 'Type',
            Type: 'EQ',
            Value: strings.basket
          }]
        },
        Order: {
          Field: 'OrderId',
          Type: 'ASC'
        }
      }).subscribe((res: ListResponse<any>) => {
        if (res.success) {
          this.orders = res.data;
          this.normalizeOrders();
          Config.basketCount = this.orders?.length ?? 0;
          this.isLoading = false;
        } else {
          SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!), res.success, res.data);
          this.isLoading = false;
        }
      }, () => {
        this.isLoading = false;
      });
    } else {
      this.loadLocalBasket();
      this.isLoading = false;
    }
  }

  calculatePriceSum() {
    if (!this.hasAccount()) {
      this.loadLocalBasket();
    }
    let totalPriceSum = 0;
    let totalAdditivePriceSum = 0;
    for (const order of this.orders ?? []) {
      totalPriceSum += parseFloat(order.TotalPrice || '0');
      totalAdditivePriceSum += parseFloat(order.TotalAdditivePrice || '0');
    }
    return totalPriceSum + totalAdditivePriceSum;
  }

  getOrderTotalPrice(order: any): number {
    const totalAdditivePrice = parseFloat(order.TotalAdditivePrice || '0');
    const totalPrice = parseFloat(order.TotalPrice || '0');
    return totalAdditivePrice + totalPrice;
  }

  getRemainingAmountUntilMinimum() {
    const currentTotal = this.calculatePriceSum();
    const minimumAmount = this.getMinimumPurchaseAmount();
    const remaining = minimumAmount - currentTotal;
    return Math.max(0, remaining);
  }

  getMinimumPurchaseAmount() {
    return Config.branch.Details?.MinimumBasket || 0;
  }

  getProgressPercentage() {
    const currentTotal = this.calculatePriceSum();
    const minimumAmount = this.getMinimumPurchaseAmount();
    if (minimumAmount === 0) return 0;
    return Math.min(100, (currentTotal / minimumAmount) * 100);
  }

  onUpdateOrderCountClicked(order: any, count: number) {
    if (this.hasAccount()) {
      this.serviceApi.postOrders({
        ServicePriceId: order.ServicePriceId,
        Count: count,
        ServicePriceAdditiveIds: order.ServicePriceAdditiveIds,
        Type: strings.basket,
        BranchId: Config.branch.BusinessId
      }).subscribe((res: DataResponse<any>) => {
        if (res.success) {
          this.ordersChange.emit();
          this.loadOnline();
        } else {
          SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!), res.success, res.data);
        }
      });
    } else {
      let basket: any[] = SessionStorageUtil.loadObject(strings.basket_service_ids, []);
      let basketServicePrice: any = basket.find((basketServicePrice: any) => {
        return order.ServicePriceId === basketServicePrice.ServicePriceId;
      });
      if (basketServicePrice) {
        basketServicePrice.Count = count;
      }
      basket = basket.filter((basketServicePrice: any) => {
        return basketServicePrice.Count !== 0;
      });
      SessionStorageUtil.saveObject(strings.basket_service_ids, basket);
      this.ordersChange.emit();
      this.loadLocalBasket();
    }
  }

  openAdditivesClicked(order: any) {
    const servicePrice = order.ServicePrice;
    if (!servicePrice?.ServicePriceAdditives?.length) {
      return;
    }
    const service = {
      ...servicePrice.Service,
      ServicePrices: [servicePrice]
    };
    const selectedAdditives = order.ServicePriceAdditiveIds ||
      (order.OrderServicePriceAdditives?.map((osa: any) => osa.ServicePriceAdditive?.ServicePriceAdditiveId) || []);
    const dialogRef = this.matDialog.open(SelectAdditivesComponent, {
      width: this.isMobile ? '100%' : '500px',
      height: this.isMobile ? 'auto' : 'auto',
      panelClass: this.isMobile ? 'mobile-dialog-panel' : 'desktop-dialog-panel',
      data: {
        Service: service,
        selectedAdditives: selectedAdditives,
        isReadOnly: true
      }
    });
    dialogRef.afterClosed().subscribe(() => {
    });
  }

  updateOrderAdditives(order: any, additives: any[]) {
    if (this.hasAccount()) {
      this.serviceApi.postOrders({
        ServicePriceId: order.ServicePriceId,
        Count: order.Count,
        ServicePriceAdditiveIds: additives,
        Type: strings.basket,
        BranchId: Config.branch.BusinessId
      }).subscribe((res: DataResponse<any>) => {
        if (res.success) {
          this.ordersChange.emit();
          this.loadOnline();
        } else {
          SnackBarUtil.makeMessage(this, this.translateService.instant(res.message!), res.success, res.data);
        }
      });
    } else {
      let basket: any[] = SessionStorageUtil.loadObject(strings.basket_service_ids, []);
      let basketServicePrice = basket.find((basketServicePrice: any) => {
        return order.ServicePriceId === basketServicePrice.ServicePriceId &&
          isEqual(basketServicePrice.ServicePriceAdditiveIds, order.ServicePriceAdditiveIds);
      });
      if (basketServicePrice) {
        basketServicePrice.ServicePriceAdditiveIds = additives;
        const additivesList = basketServicePrice.ServicePriceAdditives.filter((additive: any) =>
          additives.includes(additive.ServicePriceAdditiveId)
        );
        const additiveSum = additivesList.reduce((sum: number, additive: any) => {
          return sum + (parseFloat(additive.Price || 0));
        }, 0);
        const unitPrice = parseFloat(basketServicePrice.Price || basketServicePrice.NewPrice || 0);
        const count = basketServicePrice.Count || 1;
        basketServicePrice.TotalAdditivePrice = additiveSum * count;
        basketServicePrice.TotalPrice = unitPrice * count;
      }
      SessionStorageUtil.saveObject(strings.basket_service_ids, basket);
      this.ordersChange.emit();
      this.loadLocalBasket();
    }
  }

  loadLocalBasket() {
    let basket: any[] = SessionStorageUtil.loadObject(strings.basket_service_ids, []);
    basket = basket.map((basketServicePrice: any, index: number) => {
      const additives = basketServicePrice.ServicePriceAdditives?.filter((additive: any) =>
        basketServicePrice.ServicePriceAdditiveIds?.includes(additive.ServicePriceAdditiveId)
      ) || [];
      const additiveSum = additives.reduce((sum: number, additive: any) => {
        return sum + (parseFloat(additive.Price || 0));
      }, 0);
      const unitPrice = parseFloat(basketServicePrice.Price || basketServicePrice.NewPrice || 0);
      const count = basketServicePrice.Count || 1;
      const totalAdditivePrice = additiveSum * count;
      const totalPrice = unitPrice * count;
      const servicePrice = {
        ...basketServicePrice,
        ServicePriceId: basketServicePrice.ServicePriceId,
        Service: basketServicePrice.ServicePrice?.Service || basketServicePrice.Service,
        ServicePriceAdditives: basketServicePrice.ServicePriceAdditives || []
      };
      return {
        OrderId: index,
        ServicePriceId: basketServicePrice.ServicePriceId,
        ServicePrice: servicePrice,
        ServicePriceAdditiveIds: basketServicePrice.ServicePriceAdditiveIds || [],
        Count: count,
        OrderServicePriceAdditives: additives,
        TotalAdditivePrice: totalAdditivePrice,
        TotalPrice: totalPrice
      };
    });
    this.orders = basket;
    Config.basketCount = basket.length;
    this.isLoading = false;
  }
}
